import { scheduleProactiveRefresh, cancelProactiveRefresh } from "./token.service";

let _activeEventSource = null;
let _reconnectTimer = null;
let _reconnectDelay = 0;

const RECONNECT_MIN_MS = 2000;
const RECONNECT_MAX_MS = 60000;

const STORAGE_KEYS = [
  "token", "refresh", "apollo-token", "userData", "user", "sourcelink", "auth_provider", "session_id"
];

async function registerSession(accessToken) {
  try {
    const response = await fetch(
      `${process.env.VUE_APP_GATEWAY_URL}auth/session/register`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` }
      }
    );
    if (response.ok) {
      const data = await response.json();
      if (data.session_id) {
        localStorage.setItem("session_id", data.session_id);
      }
      if (data.previous_session_kicked) {
        console.info("[Session] A previous session was closed.");
      }
    }
  } catch (err) {
    console.error("[Session] Failed to register session:", err);
  }
}

// Always reads the token from localStorage so reconnects pick up whatever
// the proactive/reactive refresh stored last.
function openSessionStream() {
  closeSessionStream();
  const token = localStorage.getItem("token");
  if (!token) return;

  const sid = localStorage.getItem("session_id") || "";
  const url = `${process.env.VUE_APP_GATEWAY_URL}auth/session/events`
    + `?token=${encodeURIComponent(token)}&sid=${encodeURIComponent(sid)}`;
  const source = new EventSource(url);
  _activeEventSource = source;

  source.onopen = () => {
    _reconnectDelay = 0;
  };

  source.onmessage = (e) => {
    try {
      const payload = JSON.parse(e.data);
      if (payload.event === "force_logout") {
        handleForceLogout(sid);
      }
    } catch (err) {
      console.error("[Session] Error parsing SSE message:", err);
    }
  };

  // The stream must stay up for the kick to arrive, so any drop (network
  // blip, expired token -> 403, server restart) is retried with backoff.
  source.onerror = () => {
    if (_activeEventSource !== source) return;
    closeSessionStream();
    _scheduleReconnect();
  };
}

function _scheduleReconnect() {
  if (!localStorage.getItem("token")) return;
  _reconnectDelay = Math.min(
    RECONNECT_MAX_MS,
    _reconnectDelay ? _reconnectDelay * 2 : RECONNECT_MIN_MS
  );
  _reconnectTimer = setTimeout(() => {
    _reconnectTimer = null;
    openSessionStream();
  }, _reconnectDelay);
}

function closeSessionStream() {
  if (_reconnectTimer) {
    clearTimeout(_reconnectTimer);
    _reconnectTimer = null;
  }
  if (_activeEventSource) {
    _activeEventSource.close();
    _activeEventSource = null;
  }
}

function handleForceLogout(streamSid) {
  // Tabs share localStorage: if another tab of this same browser has just
  // logged in again, the kick is for the old login, not the one now stored.
  const currentSid = localStorage.getItem("session_id");
  if (streamSid && currentSid && currentSid !== streamSid) {
    openSessionStream();
    return;
  }
  cancelProactiveRefresh();
  closeSessionStream();
  STORAGE_KEYS.forEach(key => localStorage.removeItem(key));
  window.location.href = "/login?reason=session_expired";
}

function _startWatching() {
  openSessionStream();

  // Start proactive refresh — reconnect SSE with the new token each time
  scheduleProactiveRefresh(() => {
    openSessionStream();
  });
}

async function onLoginSuccess(accessToken) {
  await registerSession(accessToken);
  _startWatching();
}

// Called on app boot: a page reload drops the EventSource, and without this
// the tab would never hear about a login from another device.
function resumeSession() {
  if (localStorage.getItem("token")) {
    _startWatching();
  }
}

// Mobile browsers kill background connections without always firing onerror.
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState !== "visible") return;
  if (!localStorage.getItem("token")) return;
  if (!_activeEventSource || _activeEventSource.readyState === EventSource.CLOSED) {
    openSessionStream();
  }
});

function onLogout() {
  cancelProactiveRefresh();
  closeSessionStream();
  localStorage.removeItem("session_id");
}

export const sessionService = {
  onLoginSuccess,
  onLogout,
  resumeSession,
  closeSessionStream,
};
